/**
 * Bot signal plugin: emits a lightweight `bot_signal` event once per tab session.
 *
 * This is intentionally heuristic and privacy-safe:
 * - Does not include full user agent, IP, or fingerprints
 * - Only emits derived booleans + a coarse score
 */
import type { PluginContext } from "../lib/types";

import { registerPlugin } from "./register";

function getSessionFlag(key: string): boolean {
  try {
    if (typeof sessionStorage === "undefined") return false;
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, "1");
    return false;
  } catch {
    return false;
  }
}

registerPlugin("bot", (ctx: PluginContext) => {
  const flagKey = `prospix_bot_signal_${ctx.siteKey || "default"}`;

  const compute = () => {
    const nav = typeof navigator === "undefined" ? undefined : navigator;
    const ua = String(nav?.userAgent ?? "");
    const webdriver = Boolean((nav as any)?.webdriver);
    const uaHeadless =
      /HeadlessChrome/i.test(ua) ||
      /PhantomJS/i.test(ua) ||
      /Nightmare/i.test(ua);
    const uaBot = /\bbot\b|\bcrawler\b|\bspider\b/i.test(ua);
    const hasPlugins = Array.isArray(nav?.plugins)
      ? nav.plugins.length > 0
      : true;

    const score = [webdriver, uaHeadless, uaBot, !hasPlugins].filter(
      Boolean,
    ).length;

    return {
      score,
      webdriver: webdriver || undefined,
      ua_headless: uaHeadless || undefined,
      ua_bot: uaBot || undefined,
      no_plugins: hasPlugins ? undefined : true,
    } as Record<string, unknown>;
  };

  return {
    onInit() {
      if (!ctx.canTrack("essential")) return;
      if (!ctx.canTrackCategory("bot_signals")) return;
      if (getSessionFlag(flagKey)) return;
      ctx.track("bot_signal", compute());
    },
  };
});
